import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  useProjectRolePermissions,
  useUpdateProjectRolePermission,
} from "@/hooks/use-project-role-permissions";
import {
  PROJECT_ROLES,
  DATA_RIGHTS,
  ACTIONS,
  dataLevel,
  hasPermission,
  type ProjectRoleKey,
  type ProjectPermissionKey,
  type DataLevel,
} from "@/lib/project-permissions";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { showUndoToast } from "@/lib/undo-toast";
import { useGridNavigation, type GridCellProps } from "@/hooks/use-grid-navigation";

/**
 * VIB-1166 (fase 3): Prosjektroller-matrise med Se/Endre-nivåer.
 *
 * «Innsyn & data» rendres med to bokser per rolle (Se / Endre); «Endre
 * forutsetter Se» → ved Endre vises Se som implisitt (stiplet, ikke klikkbar).
 * «Handlinger» er enkel på/av. Nivå/innvilgelse leses fra og lagres til
 * `project_role_permissions` via view- og edit-nøklene (modell fra VIB-1164).
 */
export function ProsjektrollerCard() {
  const { data: perms, isLoading } = useProjectRolePermissions();
  const update = useUpdateProjectRolePermission();

  // Sett av innvilgede nøkler per rolle.
  const grantedByRole = useMemo(() => {
    const map = new Map<ProjectRoleKey, Set<ProjectPermissionKey>>();
    for (const r of PROJECT_ROLES) map.set(r.key, new Set());
    for (const p of perms ?? []) {
      if (p.granted) map.get(p.role)?.add(p.permission_key);
    }
    return map;
  }, [perms]);

  // VIB-2545: roving tabindex over hele matrisen. Datarader har to bokser per
  // rolle (Se = 2·i, Endre = 2·i+1); handlingsrader bruker kun Se-plassen.
  const dataRows = DATA_RIGHTS.length;
  const grid = useGridNavigation({
    rows: dataRows + ACTIONS.length,
    cols: PROJECT_ROLES.length * 2,
    isEnabled: (r, c) => {
      const role = PROJECT_ROLES[Math.floor(c / 2)];
      if (!role) return false;
      const set = grantedByRole.get(role.key) ?? new Set();
      if (r < dataRows) {
        const level = dataLevel(set, DATA_RIGHTS[r].domain);
        return c % 2 === 0 ? level !== 2 : true; // Se er låst (implisitt) når Endre er på
      }
      return c % 2 === 0 && !!ACTIONS[r - dataRows];
    },
  });

  if (isLoading) return null;

  const onError = (err: unknown) => toast.error((err as Error).message);
  const roleLabel = (role: ProjectRoleKey) =>
    PROJECT_ROLES.find((r) => r.key === role)?.label ?? role;

  const setKey = (
    role: ProjectRoleKey,
    key: ProjectPermissionKey,
    granted: boolean,
    onSuccess?: () => void,
  ) =>
    update.mutate({ role, permission_key: key, granted }, { onError, onSuccess });

  // Sett Se/Endre-nivå for en data-rettighet (oppdaterer view- og edit-nøkkel).
  const setLevel = (
    role: ProjectRoleKey,
    domain: string,
    level: DataLevel,
    onSuccess?: () => void,
  ) => {
    const right = DATA_RIGHTS.find((d) => d.domain === domain);
    if (!right) return;
    // 0: ingen · 1: kun Se (view) · 2: Endre (edit; Se implisitt → view false)
    setKey(role, right.viewKey, level === 1);
    setKey(role, right.editKey, level === 2, onSuccess);
  };

  // VIB-2530: angre-toast etter hver endring; angre setter forrige nivå
  // tilbake som en ny skriving (egen rad i audit-loggen).
  const changeLevel = (role: ProjectRoleKey, domain: string, from: DataLevel, to: DataLevel) => {
    const label = `${roleLabel(role)} – ${DATA_RIGHTS.find((d) => d.domain === domain)?.label ?? domain}`;
    setLevel(role, domain, to, () =>
      showUndoToast(`${label}: lagret`, () =>
        setLevel(role, domain, from, () => toast.success(`${label}: angret`)),
      ),
    );
  };

  const toggleSe = (role: ProjectRoleKey, domain: string, level: DataLevel) => {
    if (level === 2) return; // Se er låst når Endre er på
    changeLevel(role, domain, level, level === 1 ? 0 : 1);
  };
  const toggleEndre = (role: ProjectRoleKey, domain: string, level: DataLevel) =>
    changeLevel(role, domain, level, level === 2 ? 1 : 2);

  const toggleAction = (role: ProjectRoleKey, key: ProjectPermissionKey, on: boolean) => {
    const label = `${roleLabel(role)} – ${ACTIONS.find((a) => a.key === key)?.label ?? key}`;
    setKey(role, key, !on, () =>
      showUndoToast(`${label}: lagret`, () =>
        setKey(role, key, on, () => toast.success(`${label}: angret`)),
      ),
    );
  };

  // Teller per rolle: antall rettigheter rollen har minst Se / på.
  const roleCount = (role: ProjectRoleKey): number => {
    const set = grantedByRole.get(role) ?? new Set();
    const dataN = DATA_RIGHTS.filter((d) => dataLevel(set, d.domain) >= 1).length;
    const actN = ACTIONS.filter((a) => hasPermission(set, a.key)).length;
    return dataN + actN;
  };
  const totalRights = DATA_RIGHTS.length + ACTIONS.length;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <SrIcon name="briefcase" className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-base">Rettigheter per prosjektrolle</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Gjelder kun innenfor det enkelte prosjektet. <strong>Endre</strong> forutsetter
          alltid <strong>Se</strong>.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" role="grid" aria-label="Rettigheter per prosjektrolle">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-2 font-medium min-w-[220px]">Rettighet</th>
                {PROJECT_ROLES.map((r) => (
                  <th key={r.key} className="px-2 py-2 text-center font-medium min-w-[120px]">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: r.color }}
                      />
                      {r.label}
                    </span>
                    <span className="block text-[11px] font-normal text-muted-foreground tabular-nums">
                      {roleCount(r.key)}/{totalRights}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* ── Innsyn & data ── */}
              <tr>
                <td colSpan={1 + PROJECT_ROLES.length} className="pt-4 pb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    Innsyn &amp; data
                  </span>
                </td>
              </tr>
              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                <td />
                {PROJECT_ROLES.map((r) => (
                  <td key={r.key} className="px-2 pb-1">
                    <div className="flex justify-center gap-4">
                      <span className="w-6 text-center">Se</span>
                      <span className="w-6 text-center">Endr</span>
                    </div>
                  </td>
                ))}
              </tr>
              {DATA_RIGHTS.map((d, rowIdx) => (
                <tr key={d.domain} className="border-t border-border/40">
                  <td className="py-2 pr-2">{d.label}</td>
                  {PROJECT_ROLES.map((r, roleIdx) => {
                    const level = dataLevel(grantedByRole.get(r.key) ?? new Set(), d.domain);
                    return (
                      <td key={r.key} className="px-2 py-2" role="gridcell">
                        <div className="flex justify-center gap-4">
                          <CheckBox
                            state={level === 2 ? "implicit" : level === 1 ? "se" : "off"}
                            onClick={() => toggleSe(r.key, d.domain, level)}
                            ariaLabel={`${r.label} – Se ${d.label}`}
                            cell={grid.getCellProps(rowIdx, roleIdx * 2)}
                          />
                          <CheckBox
                            state={level === 2 ? "on" : "off"}
                            onClick={() => toggleEndre(r.key, d.domain, level)}
                            ariaLabel={`${r.label} – Endre ${d.label}`}
                            cell={grid.getCellProps(rowIdx, roleIdx * 2 + 1)}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* ── Handlinger ── */}
              <tr>
                <td colSpan={1 + PROJECT_ROLES.length} className="pt-5 pb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    Handlinger
                  </span>
                </td>
              </tr>
              {ACTIONS.map((a, actIdx) => (
                <tr key={a.key} className="border-t border-border/40">
                  <td className="py-2 pr-2">{a.label}</td>
                  {PROJECT_ROLES.map((r, roleIdx) => {
                    const on = (grantedByRole.get(r.key) ?? new Set()).has(a.key);
                    return (
                      <td key={r.key} className="px-2 py-2" role="gridcell">
                        <div className="flex justify-center">
                          <CheckBox
                            state={on ? "on" : "off"}
                            onClick={() => toggleAction(r.key, a.key, on)}
                            ariaLabel={`${r.label} – ${a.label}`}
                            cell={grid.getCellProps(dataRows + actIdx, roleIdx * 2)}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

type BoxState = "off" | "se" | "implicit" | "on";

/** Avkryssingsboks med Sølvrev-tilstander: av / Se / Se-implisitt / på (Endre). */
function CheckBox({
  state,
  onClick,
  ariaLabel,
  cell,
}: {
  state: BoxState;
  onClick: () => void;
  ariaLabel: string;
  /** VIB-2545: roving tabindex + piltaster fra useGridNavigation. */
  cell: GridCellProps;
}) {
  const implicit = state === "implicit";
  const { ref, ...cellHandlers } = cell;
  return (
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={state !== "off"}
      aria-label={ariaLabel}
      disabled={implicit}
      onClick={onClick}
      {...cellHandlers}
      className={cn(
        "w-[22px] h-[22px] rounded-[6px] border-[1.5px] flex items-center justify-center transition-colors",
        state === "off" && "border-foreground/20 bg-transparent hover:border-foreground/40",
        state === "se" && "border-[#3f9ec0] bg-[#50bdd8]/15 text-[#1f7a96]",
        implicit && "border-dashed border-[#3f9ec0]/65 bg-[#50bdd8]/10 text-[#7fb6c9] cursor-not-allowed",
        state === "on" && "border-primary bg-primary text-primary-foreground",
      )}
    >
      {state !== "off" && <SrIcon name="check" className="w-3 h-3" />}
    </button>
  );
}
